import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

type ShowroundParamsValidation =
  | {
      hasSchedulingParams: true;
      actionType: 'schedule';
      params: {
        schedule?: string;
        event?: string;
      };
    }
  | {
      hasSchedulingParams: true;
      actionType: 'cancel';
      params: {
        cancel: string;
        calendarId: string;
      };
    }
  | {
      hasSchedulingParams: true;
      actionType: 'reconfirm';
      params: {
        reconfirm: string;
        calendarId: string;
      };
    }
  | {
      hasSchedulingParams: true;
      actionType: 'directReconfirm';
      params: {
        directReconfirm: string;
        calendarId: string;
      };
    }
  | {
      hasSchedulingParams: true;
      actionType: 'feedback';
      params: {
        feedback: string;
        calendarId: string;
      };
    }
  | {
      hasSchedulingParams: false;
      actionType: null;
      params: Record<string, never>;
    };

export const useParseShowroundQueryParams = (): ShowroundParamsValidation => {
  const router = useRouter();
  return parseShowroundParams(router.query);
};

const parseShowroundParams = (queryParams: ParsedUrlQuery): ShowroundParamsValidation => {
  const schedule = queryParams.schedule; // calendarId
  const event = queryParams.event || queryParams.events; // eventId or 'all'
  const cancel = queryParams.cancel; // eventId
  const reconfirm = queryParams.reconfirm; // eventId
  const directReconfirm = queryParams.directReconfirm; // eventId
  const feedback = queryParams.feedback; // eventId
  const calendarId = queryParams.calendar; // calendarId

  // Check for schedule action
  if (typeof schedule === 'string' || typeof event === 'string') {
    return {
      hasSchedulingParams: true,
      actionType: 'schedule',
      params: {
        schedule: typeof schedule === 'string' ? schedule : undefined,
        event: typeof event === 'string' ? event : undefined,
      },
    };
  }

  // Check for cancel action (requires both cancel and calendarId)
  if (typeof cancel === 'string' && typeof calendarId === 'string') {
    return {
      hasSchedulingParams: true,
      actionType: 'cancel',
      params: {
        cancel,
        calendarId,
      },
    };
  }

  // Check for reconfirm action (requires eventId and calendarId)
  if (typeof reconfirm === 'string' && typeof calendarId === 'string') {
    return {
      hasSchedulingParams: true,
      actionType: 'reconfirm',
      params: {
        reconfirm,
        calendarId,
      },
    };
  }

  if (typeof directReconfirm === 'string' && typeof calendarId === 'string') {
    return {
      hasSchedulingParams: true,
      actionType: 'directReconfirm',
      params: {
        directReconfirm,
        calendarId,
      },
    };
  }

  // Check for feedback action (requires eventId and calendarId)
  if (typeof feedback === 'string' && typeof calendarId === 'string') {
    return {
      hasSchedulingParams: true,
      actionType: 'feedback',
      params: {
        feedback,
        calendarId,
      },
    };
  }

  // No valid scheduling params found
  return {
    hasSchedulingParams: false,
    actionType: null,
    params: {} as Record<string, never>,
  };
};
